
import { action, observable } from 'mobx';
import * as Models from 'Models/Entities';
import {
	attribute,
	entity,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserEsigntemplatecontent from 'Models/Security/Acl/UserEsigntemplatecontent';
import VisitorsEsigntemplatecontent from 'Models/Security/Acl/VisitorsEsigntemplatecontent';
import { IAcl } from 'Models/Security/IAcl';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
	makeFetchOneToManyFunc,
} from 'Util/EntityUtils';
import * as Validators from 'Validators';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IEsigntemplatecontentEntityAttributes extends IModelAttributes {
	version: number;
	content: string;
	requireswitness: boolean;
	usesmsauth: boolean;
	minimumNumberOfSignees?: number;

	esigntemplateId: string;
	esigntemplate: Models.EsigntemplateEntity | Models.IEsigntemplateEntityAttributes;
	esignworkflows: Array<
		| Models.EsignworkflowEntity
		| Models.IEsignworkflowEntityAttributes
	>;

}

@entity('EsigntemplatecontentEntity', 'ESignTemplateContent')
export default class EsigntemplatecontentEntity extends Model
	implements IEsigntemplatecontentEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsEsigntemplatecontent(),
		new UserEsigntemplatecontent(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	
	@Validators.Integer()
	@observable
	@attribute<EsigntemplatecontentEntity, number>()
	@CRUD({
		name: 'Version',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public version: number;

	@observable
	@attribute<EsigntemplatecontentEntity, string>()
	@CRUD({
		name: 'Content',
		displayType: 'textarea',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public content: string;

	@observable
	@attribute<EsigntemplatecontentEntity, boolean>()
	@CRUD({
		name: 'RequiresWitness',
		displayType: 'checkbox',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public requireswitness: boolean;

	@observable
	@attribute<EsigntemplatecontentEntity, boolean>()
	@CRUD({
		name: 'UseSMSAuth',
		displayType: 'checkbox',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public usesmsauth: boolean;

	@Validators.Integer()
	@observable
	@attribute<EsigntemplatecontentEntity, number>()
	@CRUD({
		name: 'MinimumNumberOfSignees',
		displayType: 'textfield',
		order: 45,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public minimumNumberOfSignees?: number;


	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'ESignTemplate',
		displayType: 'reference-combobox',
		order: 50,
		referenceTypeFunc: () => Models.EsigntemplateEntity,
		
	})
	public esigntemplateId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public esigntemplate: Models.EsigntemplateEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'ESignWorkflows',
		displayType: 'reference-multicombobox',
		order: 60,
		referenceTypeFunc: () => Models.EsignworkflowEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'esignworkflows',
			oppositeEntity: () => Models.EsignworkflowEntity,
		}),
		
	})
	public esignworkflows: Models.EsignworkflowEntity[] = [];



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IEsigntemplatecontentEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IEsigntemplatecontentEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.version !== undefined) {
				this.version = attributes.version;
			}
			if (attributes.content !== undefined) {
				this.content = attributes.content;
			}
			if (attributes.requireswitness !== undefined) {
				this.requireswitness = attributes.requireswitness;
			}
			if (attributes.usesmsauth !== undefined) {
				this.usesmsauth = attributes.usesmsauth;
			}
			if (attributes.minimumNumberOfSignees !== undefined) {
				this.minimumNumberOfSignees = attributes.minimumNumberOfSignees;
			}
			if (attributes.esigntemplateId !== undefined) {
				this.esigntemplateId = attributes.esigntemplateId;
			}
			if (attributes.esigntemplate !== undefined) {
				if (attributes.esigntemplate === null) {
					this.esigntemplate = attributes.esigntemplate;
				} else if (attributes.esigntemplate instanceof Models.EsigntemplateEntity) {
					this.esigntemplate = attributes.esigntemplate;
					this.esigntemplateId = attributes.esigntemplate.id;
				} else {
					this.esigntemplate = new Models.EsigntemplateEntity(attributes.esigntemplate);
					this.esigntemplateId = this.esigntemplate.id;
				}
			}
			if (attributes.esignworkflows !== undefined && Array.isArray(attributes.esignworkflows)) {
				for (const model of attributes.esignworkflows) {
					if (model instanceof Models.EsignworkflowEntity) {
						this.esignworkflows.push(model);
					} else {
						this.esignworkflows.push(new Models.EsignworkflowEntity(model));
					}
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		esigntemplate {
			${Models.EsigntemplateEntity.getAttributes().join('\n')}
		}
		esignworkflows {
			${Models.EsignworkflowEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			esignworkflows: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(EsigntemplatecontentEntity.prototype, 'created');
CRUD(modifiedAttr)(EsigntemplatecontentEntity.prototype, 'modified');
